import {
  Carousel,
  CarouselIndicator,
  CarouselMainContainer,
  CarouselNext,
  CarouselPrevious,
  CarouselThumbsContainer,
  SliderMainItem,
} from '@/components/extension/carousel'
import { Accordion } from '@/components/ui/accordion'
import { Button } from '@/components/ui/button'
import { useBannerQuery } from '@/hooks/react-query/banner/use-banner'
import { ImageHelper } from '@/lib/helpers/image.helper'
import { getLastSegment, normalizeVietnamese } from '@/lib/utils'
import {
  TCategoryButtonItem,
  TCategoryFeaturedItem,
  TMenuMobileSection,
} from '@/types/header'
import Autoplay from 'embla-carousel-autoplay'
import { ChevronRightIcon } from 'lucide-react'
import { useTranslations } from 'next-intl'
import Image from 'next/image'
import Link from 'next/link'
import { MobileNavList } from './mobile-nav-list'

// Các section tương ứng footer của mega menu desktop -> gắn tracking `__footer`
const FOOTER_TRACKING_SECTIONS = [
  'nav.section.by_need',
  'nav.section.buy_by_need',
  'nav.section.explore_bst',
]

type Props = {
  listCategory: TMenuMobileSection[]
  slides: TCategoryFeaturedItem[]
  mobileBanner?: TCategoryFeaturedItem
  button?: TCategoryButtonItem
  category: string
  type?: string
}

export const MobileMenuTabItem: React.FC<Props> = ({
  slides,
  listCategory,
  mobileBanner,
  button,
  category,
  type,
}) => {
  const t = useTranslations('header')
  const { data: apiBanners } = useBannerQuery()

  const bannersForType = type && apiBanners ? apiBanners[type] : undefined

  const validBannersFromApi = bannersForType?.filter(
    (banner) => banner.link && banner.title && banner.image?.mobile
  )

  const hasApiBanners = validBannersFromApi && validBannersFromApi.length > 0

  const bannersToDisplay = hasApiBanners
    ? validBannersFromApi.map((item) => ({
        title: item.title,
        href: item.link,
        imageAlt: item.alt,
        imageSrc: item.image.mobile,
        tag: undefined,
      }))
    : slides

  if (!bannersToDisplay || bannersToDisplay.length === 0) {
    return null
  }

  return (
    <div>
      <Carousel
        carouselOptions={{
          loop: true,
        }}
        plugins={[
          Autoplay({
            delay: 4000,
            stopOnInteraction: false,
            stopOnMouseEnter: true,
          }),
        ]}
      >
        {bannersToDisplay.length > 1 && (
          <>
            <CarouselPrevious
              size='icon'
              variant={'neutral'}
              className='absolute left-0 top-1/2 z-10 h-9.5 w-9.5 -translate-x-1/2 -translate-y-1/2 bg-neutral-200 [&>svg]:stroke-neutral-800'
            />
            <CarouselNext
              size='icon'
              variant={'neutral'}
              className='absolute right-0 top-1/2 z-10 h-9.5 w-9.5 -translate-y-1/2 translate-x-1/2 bg-neutral-200 [&>svg]:stroke-neutral-800'
            />
          </>
        )}
        <CarouselMainContainer className='h-full'>
          {bannersToDisplay.map((slide, idx) => (
            <SliderMainItem
              className='relative h-full w-full p-0'
              key={slide.href}
            >
              <Link
                href={slide.href}
                ga-tracking-value={`menu__banner__${type}__vitri-${idx + 1}_${normalizeVietnamese(slide.title)}`}
                ga-tracking-label={hasApiBanners ? slide.title : t(slide.title)}
              >
                <picture className='overflow-hidden'>
                  <Image
                    src={ImageHelper.getImageUrl(slide.imageSrc, '', 584)}
                    alt={slide.imageAlt || `Menu mobile slide`}
                    width={292}
                    height={128}
                    className='aspect-[292/100] min-h-32 w-full rounded object-cover max-lg:object-top'
                  />
                </picture>
                {slide?.title !== '' ? (
                  <p className='absolute bottom-0 line-clamp-1 w-full rounded bg-gradient-to-b from-transparent to-neutral-900/60 px-4 pb-3 pt-5 font-sans text-sm font-bold text-light'>
                    {hasApiBanners ? slide.title : t(slide.title)}
                  </p>
                ) : null}
              </Link>
            </SliderMainItem>
          ))}
        </CarouselMainContainer>

        {button && (
          <Link
            href={button.href}
            ga-tracking-value={`menu__${type}__cta__${getLastSegment(button.text)}`}
            ga-tracking-label={t(button.text)}
            className='mt-2 rounded-lg bg-neutral-100 p-2 text-center font-criteria text-xs uppercase leading-3.5 text-neutral-900'
          >
            {t(button.text)}
          </Link>
        )}

        <div className='hidden'>
          <CarouselThumbsContainer>
            {bannersToDisplay.map((_, index) => (
              <CarouselIndicator key={index} index={index} />
            ))}
          </CarouselThumbsContainer>
        </div>
      </Carousel>
      <Accordion
        type='single'
        collapsible
        className='w-full'
        defaultValue={listCategory[0]?.title}
      >
        {listCategory.map((category, idx) => (
          <MobileNavList
            title={category.title}
            key={idx}
            list={category.items}
            type={type || '**'}
            gaType={
              FOOTER_TRACKING_SECTIONS.includes(category.title)
                ? `${type || '**'}__footer`
                : undefined
            }
          />
        ))}
      </Accordion>

      {mobileBanner && (
        <Link
          href={mobileBanner.href}
          className='relative mt-3'
          ga-tracking-value={`menu__${type}__secondary-banner`}
          ga-tracking-label={
            hasApiBanners ? mobileBanner.title : t(mobileBanner.title)
          }
        >
          <picture>
            <source media='(max-width: 768px)' srcSet={mobileBanner.imageSrc} />
            <Image
              src={mobileBanner.imageSrc}
              alt={mobileBanner.imageAlt || `Menu mobile mobileBanner`}
              width={292}
              height={65}
              className='aspect-[340/185] max-h-[65px] w-full rounded-xl object-fill'
            />
          </picture>
          <div className='absolute top-1/2 flex w-full -translate-y-1/2 items-center justify-between px-5'>
            <p className='left-4 line-clamp-1 font-sans text-sm font-semibold uppercase text-light'>
              {hasApiBanners ? mobileBanner.title : t(mobileBanner.title)}
            </p>

            <Button
              variant='neutral'
              size={'icon'}
              className='bg-light hover:bg-neutral-200'
            >
              <ChevronRightIcon
                strokeWidth={3}
                className='!size-6 stroke-neutral-500 group-hover:stroke-light'
              />
            </Button>
          </div>
        </Link>
      )}
    </div>
  )
}
